// @ts-check
import { get, getAll } from "../utilities/querySelector.js";

/**
 * HTML Structure should be as follows:
 *
 * <nu-paged-form class="paged-form" page="1">
 *   <... class="paged-form__header">...<...>
 *   <... class="form-page" data-page data-page-active>...</...>
 *   <... data-page>...</...>
 *   <... data-page>...</...>
 *   <... class="page-form__footer">...</...>
 * </nu-page-form>
 */
class PageFormHTMLElement extends HTMLElement {

    static formNumber = 0;

    static observedAttributes = ['page'];

    constructor() {
        super();
        this.formNumber = PageFormHTMLElement.formNumber;
        PageFormHTMLElement.formNumber++;
        if ( !this.id ) {
            this.id = `paged-form-${this.formNumber}`;
        }

        this.page = 1;
        this.pages = getAll( '[data-page]', this );
        this.pages[0].setAttribute( 'data-page-active', '' );

        this.prevButton = get( '[data-page-prev]', this );
        this.nextButton = get( '[data-page-next]', this );

        this.prevButton?.style.setProperty( 'display', 'none' );
    }

    get currentPage() {
        return this.pages[this.page - 1];
    }

    get page() {
        return this._page;
    }


    set page( num ) {
        this.setAttribute( 'page', num );
        this._page = num;
    }


    attributeChangedCallback( attr, oldValue, newValue ) {
        if ( attr === 'page' ) {
            this.pages[oldValue - 1].removeAttribute( 'data-page-active' );
            this.pages[newValue - 1].setAttribute( 'data-page-active', '' );

            this.setButtonStates( parseInt( newValue ) );
        }
    }


    connectedCallback() {
        this.nextButton.addEventListener( 'click', this.next.bind( this ) );
        this.prevButton.addEventListener( 'click', this.prev.bind( this ) );
    }


    disconnectedCallback() {

    }

    /**
     * Checks the validity of required inputs on the current page
     * Only checks whether the 'required' input actually has a value or not
     *
     * @returns {boolean}
     */
    validatePage() {
        let valid = true;
        const requiredInputs = getAll( '[required], [aria-required]', this.currentPage );

        for ( const input of requiredInputs ) {
            // Is the input actually displayed?
            // Conditional inputs won't be so we don't want to prevent page navigation if they're not visible
            if ( input.offsetHeight === 0 && input.offsetWidth === 0 ) continue;

            if ( !input.value ) {
                valid = false;
                input.classList.add( 'invalid' );
            } else {
                input.classList.remove( 'invalid' );
            }
        }
        return valid;
    }


    setButtonStates( pageNum = this.page ) {
        if ( pageNum === 1 ) {
            this.prevButton?.style.setProperty( 'display', 'none' );
        } else {
            this.prevButton?.style.removeProperty( 'display' );
        }

        if ( pageNum >= this.pages.length ) {
            this.nextButton.textContent = 'Submit';
            this.nextButton.id = `${this.id}-submit`;

            // Not happy about this but it's the solution that works :(
            // For bleeding tracking purposes
            if ( this.nextButton.id = 'support-form-submit' ) {
                this.nextButton.id = 'support-submit-block';
            }
        } else {
            this.nextButton.textContent = 'Next';
            this.nextButton?.removeAttribute( 'id' );
        }
    }

    next() {
        if ( !this.validatePage() ) {
            return;
        }
        if ( this.page < this.pages.length ) this.page++;
        else {
            const form = this.nextButton.form;
            form.requestSubmit();
        }
    }

    prev() {
        if ( this.page > 1 ) this.page--;
    }
}

customElements.define( 'nu-paged-form', PageFormHTMLElement );
